import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Chip } from 'primereact/chip';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateForApi, formatDateWithTime } from '../../../../utils/dateUtils';
import { useGetLmsCreateRequestByIdQuery, useGetTrainingTypesByVehicleModelQuery, useGetVehicleModelsWithUpcomingPlanningsQuery, useGetLmsCreateRequestPlanningsQuery } from '../../../../api';
import UserDropdown from '../../../../components/common/UserDropdown';
import ExpandableNameList from './ExpandableNameList';

// "Yayınlanan Eğitimler" akışı için — bkz. "docs/LMS EĞİTİM TALEBİ_new-changed.txt" madde 3.
// `requestType` BE tarafından doğrulandı: 1 = Yayınlanan Eğitimler, 2 = Talep Edilen Eğitimler
// (lookup_translation_t REQUEST_TYPE grubu, Add/Update bu gruba karşı doğruluyor) — artık varsayım
// değil.
const REQUEST_TYPE = 1;

const toRequestDateTime = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T09:00:00`;
};

const emptyFormData = () => ({
    id: null,
    vehicleModel: null,
    trainingType: null,
    planningId: null,
    numberOfParticipants: null,
    participants: [],
    description: '',
    commitmentApproval: 0,
    status: 0,
    isActive: 1
});

const PublishedTrainingRequestForm = ({ visible, onHide, onSubmit, initialData, loading, handleDelete, readOnly = false }) => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const isEdit = !!(initialData && (initialData.requestId || initialData.id));
    const editId = isEdit ? initialData.requestId ?? initialData.id : null;

    const { data: detailData, isLoading: isLoadingDetail } = useGetLmsCreateRequestByIdQuery(editId, { skip: !isEdit || !visible });
    const sourceData = isEdit ? detailData || initialData : null;

    const [formData, setFormData] = useState(emptyFormData());
    const [localSubmitted, setLocalSubmitted] = useState(false);
    // Düzenleme modunda: kayıttaki ham preferTrainingDate/location/trainingLanguage, aşağıdaki
    // planlama listesiyle eşleştirilemezse (ör. o planlama artık 7+ gün penceresinin dışında/silinmiş)
    // gösterilecek yedek bilgi — bkz. aşağıdaki eşleştirme efekti.
    const [fallbackPlanningInfo, setFallbackPlanningInfo] = useState(null);
    const [planningAutoMatchDone, setPlanningAutoMatchDone] = useState(false);

    // Adım 1: Araç Modeli — yalnızca katalogda aktif kaydı olan VE en az bir planlaması 7+ gün sonra
    // başlayan modelleri döner (bkz. "docs/LMS EĞİTİM TALEBİ_new-changed.txt" madde 5). Önceki
    // "GEÇİCİ sınırsız liste" iş kuralı artık gerçek BE ucuyla değiştirildi.
    const { data: vehicleModelsData, isFetching: isLoadingVehicleModels } = useGetVehicleModelsWithUpcomingPlanningsQuery(undefined, { skip: !visible });
    const vehicleModelOptions = useMemo(() => {
        const options = (vehicleModelsData || []).map((item) => ({ value: item.vehicleModel, label: item.vehicleModelText || item.vehicleModel }));
        // Düzenleme modunda eski bir kayıt açıldığında, o kaydın araç modelinin artık 7+ gün
        // sonrası bir planlaması kalmamış olabilir (bu yüzden listede görünmez) — seçili değer
        // listede yoksa boş görünmesin diye ham koduyla ekleniyor.
        if (formData.vehicleModel && !options.some((opt) => opt.value === formData.vehicleModel)) {
            options.push({ value: formData.vehicleModel, label: formData.vehicleModel });
        }
        return options;
    }, [vehicleModelsData, formData.vehicleModel]);

    // Adım 2: Eğitim Türü — 7+ gün kuralını sunucu tarafında uygulayan mevcut uç (değişmedi).
    const { data: trainingTypesByVehicleModel, isFetching: isLoadingTrainingTypes } = useGetTrainingTypesByVehicleModelQuery(formData.vehicleModel, { skip: !visible || !formData.vehicleModel });
    const trainingTypeOptions = useMemo(() => (trainingTypesByVehicleModel || []).map((item) => ({ value: item.trainingType, label: item.trainingTypeText || item.trainingType })), [trainingTypesByVehicleModel]);

    // Adım 3: seçilen model+tür için 7+ gün kuralını geçen planlama alternatifleri — bkz.
    // "docs/LMS EĞİTİM TALEBİ_new-changed.txt" (GetPlannings). Her satır kendi lokasyonunu, dilini,
    // ücretini ve önkoşullarını taşıyor; önceki üç ayrı uç (LmsTrainingCatalog/GetList ile trainingId
    // çözme, LmsCatalogPlanning/GetList, LmsTrainingPrecondition/GetList) ve GetApplicableFee bu TEK
    // uçla değiştirildi.
    const { data: planningsData, isFetching: isLoadingPlannings } = useGetLmsCreateRequestPlanningsQuery(
        { vehicleModel: formData.vehicleModel, trainingType: formData.trainingType },
        { skip: !visible || !formData.vehicleModel || !formData.trainingType }
    );
    const planningOptions = useMemo(() => {
        const items = planningsData || [];
        return items
            .filter((p) => p.startDate)
            .map((p) => ({
                value: p.planningId,
                label: `${formatDateWithTime(p.startDate, false)} — ${p.planningLocationText || p.planningLocation}${p.trainingLanguage ? ' · ' + p.trainingLanguage : ''}`,
                raw: p
            }));
    }, [planningsData]);
    const selectedPlanning = useMemo(() => planningOptions.find((opt) => opt.value === formData.planningId)?.raw || null, [planningOptions, formData.planningId]);

    // Ön koşullar artık GetPlannings'in her satırıyla birlikte geliyor (bilgi amaçlı, salt okunur —
    // katılımcıların bu ön koşulları karşılayıp karşılamadığının otomatik kontrolü henüz yok, bkz.
    // docs/LMS-Egitim-Talebi-BE-Istek-Notu.md §1). Kullanıcı henüz bir tarih/planlama seçmemişse
    // (tüm satırlarda aynı olduğu için) ilk satırdan gösterilir — böylece önkoşullar tarih
    // seçilmeden önce de görülebilir, eski davranışla aynı.
    const preconditionRows = (selectedPlanning ?? planningOptions[0]?.raw)?.preconditions || [];

    // Ücret artık seçilen planlama satırından geliyor (fee/currency/isPaid) — ayrı bir
    // GetApplicableFee çağrısına gerek kalmadı.

    useEffect(() => {
        if (!visible) return;
        if (isEdit && !sourceData) return;

        if (!sourceData) {
            setFormData(emptyFormData());
            setFallbackPlanningInfo(null);
            setPlanningAutoMatchDone(false);
            setLocalSubmitted(false);
            return;
        }

        setFormData({
            id: sourceData.requestId ?? sourceData.id ?? null,
            vehicleModel: sourceData.vehicleModel ?? null,
            trainingType: sourceData.trainingType ?? null,
            planningId: null, // aşağıdaki eşleştirme efektiyle (planlama listesi yüklenince) doldurulmaya çalışılır
            numberOfParticipants: sourceData.numberOfParticipants ?? null,
            participants: (sourceData.participants || []).map((p) => ({ id: p.participantId, name: p.participantFullName, displayValue: `${p.participantId} - ${p.participantFullName}` })),
            description: sourceData.description ?? '',
            commitmentApproval: sourceData.commitmentApproval ?? 0,
            status: sourceData.status ?? 0,
            isActive: sourceData.isActive ?? 1
        });
        setFallbackPlanningInfo({
            preferTrainingDate: sourceData.preferTrainingDate ? new Date(sourceData.preferTrainingDate) : null,
            locationText: sourceData.locations?.[0]?.locationText ?? sourceData.locations?.[0]?.locationCode ?? '',
            trainingLanguage: sourceData.trainingLanguage ?? ''
        });
        setPlanningAutoMatchDone(false);
        setLocalSubmitted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isEdit, sourceData]);

    // Katılımcı Sayısı alanı artık elle girilmiyor; Katılımcılar alanında seçilen kişi sayısından
    // otomatik hesaplanıyor (kullanıcı isteği) — bkz. aşağıdaki InputNumber'ın disabled olması.
    useEffect(() => {
        setFormData((prev) => (prev.participants.length === (prev.numberOfParticipants || 0) ? prev : { ...prev, numberOfParticipants: prev.participants.length || null }));
    }, [formData.participants]);

    // Düzenleme modunda, planlama listesi yüklendiğinde kayıttaki tarih+lokasyonla eşleşen alternatifi
    // otomatik seçmeyi dener (yalnızca bir kez — kullanıcının sonradan yaptığı seçimi ezmemek için).
    useEffect(() => {
        if (!isEdit || planningAutoMatchDone || isLoadingPlannings || !planningOptions.length || !fallbackPlanningInfo?.preferTrainingDate) return;
        const targetDateStr = formatDateForApi(fallbackPlanningInfo.preferTrainingDate);
        const match = planningOptions.find((opt) => opt.raw.startDate && formatDateForApi(new Date(opt.raw.startDate)) === targetDateStr);
        if (match) {
            setFormData((prev) => (prev.planningId ? prev : { ...prev, planningId: match.value }));
        }
        setPlanningAutoMatchDone(true);
    }, [isEdit, planningAutoMatchDone, isLoadingPlannings, planningOptions, fallbackPlanningInfo]);

    // NOT: React state güncellemeleri (setLocalSubmitted) senkron değildir — handleSubmit içinde
    // setLocalSubmitted(true) çağrıldıktan hemen sonra eski isFieldInvalid çağrılırsa hâlâ eski
    // `localSubmitted=false` değeri okunur ve ilk "Kaydet" tıklamasında hiçbir alan doğrulanmadan
    // API isteği atılabilir. Bu yüzden asıl geçerlilik kararı `localSubmitted`'dan bağımsız, saf
    // bir fonksiyonla (checkFieldInvalid) veriliyor; isFieldInvalid sadece kırmızı çerçeve gösterimi
    // için (yalnızca bir kez "Kaydet"e basıldıktan sonra) kullanılıyor.
    const checkFieldInvalid = (field) => {
        if (field === 'numberOfParticipants') return !(Number(formData.numberOfParticipants) > 0);
        if (field === 'participants') return !(formData.participants?.length > 0);
        if (field === 'planningId') return !formData.planningId;
        if (field === 'commitmentApproval') return Number(formData.commitmentApproval) !== 1;
        return formData[field] == null;
    };

    const isFieldInvalid = (field) => {
        if (!localSubmitted) return false;
        return checkFieldInvalid(field);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalSubmitted(true);

        const requiredFields = ['vehicleModel', 'trainingType', 'planningId'];
        const missingRequired = requiredFields.some((field) => checkFieldInvalid(field));
        const otherInvalid = checkFieldInvalid('numberOfParticipants') || checkFieldInvalid('participants') || checkFieldInvalid('commitmentApproval');

        if (missingRequired || otherInvalid) {
            toast.current?.show({ severity: 'error', summary: th('trainingRequest.missingField'), detail: th('trainingRequest.formValidationMessage'), life: 4000 });
            return;
        }

        const payload = {
            requestType: REQUEST_TYPE,
            vehicleModel: formData.vehicleModel,
            trainingType: formData.trainingType,
            numberOfParticipants: Number(formData.numberOfParticipants),
            preferTrainingDate: selectedPlanning ? toRequestDateTime(new Date(selectedPlanning.startDate)) : null,
            alternativeDate: null,
            trainingLanguage: (selectedPlanning?.trainingLanguage || '').trim(),
            description: (formData.description || '').trim(),
            commitmentApproval: Number(formData.commitmentApproval),
            status: Number(formData.status),
            isActive: Number(formData.isActive),
            locationCodes: selectedPlanning?.planningLocation ? [selectedPlanning.planningLocation] : [],
            preconditionCodes: preconditionRows.map((row) => Number(row.preconditionCode)),
            participantIds: formData.participants.map((p) => p.id)
        };

        if (isEdit) {
            payload.id = formData.id;
        }

        onSubmit(payload);
    };

    const summaryVehicleModel = vehicleModelOptions.find((opt) => opt.value === formData.vehicleModel)?.label || formData.vehicleModel || '';
    const summaryTrainingType = trainingTypeOptions.find((opt) => opt.value === formData.trainingType)?.label || formData.trainingType || '';
    const participantNames = formData.participants.map((p) => p.name);
    const summaryPreconditions =
        preconditionRows
            .map((row) => row.preconditionText)
            .filter(Boolean)
            .join(', ') || th('trainingRequest.summary.none');
    const summaryLocation = selectedPlanning?.planningLocationText || fallbackPlanningInfo?.locationText || th('trainingRequest.summary.none');
    const summaryLanguage = selectedPlanning?.trainingLanguage || fallbackPlanningInfo?.trainingLanguage || th('trainingRequest.summary.none');
    const summaryDate = selectedPlanning?.startDate
        ? new Date(selectedPlanning.startDate).toLocaleDateString('tr-TR')
        : fallbackPlanningInfo?.preferTrainingDate
        ? fallbackPlanningInfo.preferTrainingDate.toLocaleDateString('tr-TR')
        : th('trainingRequest.summary.none');

    const summaryFee = (() => {
        if (!selectedPlanning) return th('trainingRequest.summary.none');
        if (selectedPlanning.isPaid === false) return th('trainingRequest.summary.feeFree');
        return `${selectedPlanning.fee} ${selectedPlanning.currency || ''}`.trim();
    })();

    const renderFooter = () => (
        <div>
            {isEdit && handleDelete && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" type="button" onClick={() => handleDelete(formData.id)} disabled={loading} />}
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined type="button" />
            {!readOnly && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={!!loading} />}
        </div>
    );

    const isFormReady = !isEdit || !!sourceData;
    const showNoTrainingTypesMessage = !!formData.vehicleModel && !isLoadingTrainingTypes && trainingTypeOptions.length === 0;
    const showNoPlanningsMessage = !!formData.trainingType && !isLoadingPlannings && planningOptions.length === 0;
    const showEditMismatchHint = isEdit && planningAutoMatchDone && !formData.planningId && !!fallbackPlanningInfo?.preferTrainingDate;

    return (
        <Dialog
            visible={visible}
            footer={renderFooter()}
            style={{ width: '65vw', maxWidth: '900px' }}
            header={isEdit ? th('trainingRequest.published.editTitle') : th('trainingRequest.published.addTitle')}
            modal
            className="p-fluid"
            onHide={onHide}
            key={formData.id ?? 'new'}
        >
            <Toast ref={toast} />
            {!isFormReady || isLoadingDetail ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="p-fluid">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="publishedVehicleModel">
                                    {th('trainingRequest.fields.vehicleModel')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="publishedVehicleModel"
                                    value={formData.vehicleModel || null}
                                    options={vehicleModelOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFormData((prev) => (e.value === prev.vehicleModel ? prev : { ...prev, vehicleModel: e.value, trainingType: null, planningId: null }))}
                                    placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                    loading={isLoadingVehicleModels}
                                    filter
                                    showClear
                                    className={isFieldInvalid('vehicleModel') ? 'p-invalid' : ''}
                                />
                                {showNoTrainingTypesMessage && <small className="text-orange-500">{th('trainingRequest.published.noTrainingTypesForModel')}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="publishedTrainingType">
                                    {th('trainingRequest.fields.trainingType')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="publishedTrainingType"
                                    value={formData.trainingType || null}
                                    options={trainingTypeOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFormData((prev) => ({ ...prev, trainingType: e.value, planningId: null }))}
                                    placeholder={formData.vehicleModel ? th('trainingRequest.fields.selectPlaceholder') : th('trainingRequest.fields.selectVehicleModelFirstPlaceholder')}
                                    disabled={!formData.vehicleModel}
                                    loading={isLoadingTrainingTypes}
                                    filter
                                    showClear
                                    className={isFieldInvalid('trainingType') ? 'p-invalid' : ''}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-8">
                            <div className="field">
                                <label htmlFor="publishedParticipants">
                                    {th('trainingRequest.fields.participants')} <span className="text-red-500">*</span>
                                </label>
                                {/* Bayi rolündeki kullanıcı için yalnızca kendi bayisindeki personeli listeler
                                    (Admin/WarrantyManager için tüm kullanıcılar) — bkz.
                                    "docs/LMS EĞİTİM TALEBİ_new-changed.txt" madde 4. */}
                                <UserDropdown
                                    value={formData.participants}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, participants: value }))}
                                    placeholder={th('trainingRequest.fields.participantsPlaceholder')}
                                    className={isFieldInvalid('participants') ? 'p-invalid' : ''}
                                    dealerScoped
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="publishedNumberOfParticipants">
                                    {th('trainingRequest.fields.numberOfParticipants')} <span className="text-red-500">*</span>
                                </label>
                                {/* Katılımcı Sayısı artık Katılımcılar alanından seçilen kişi sayısından
                                    otomatik hesaplanıyor ve salt okunur (kullanıcı isteği). */}
                                <InputNumber id="publishedNumberOfParticipants" value={formData.numberOfParticipants} min={1} disabled className={isFieldInvalid('numberOfParticipants') ? 'p-invalid' : ''} />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field">
                                <label>{th('trainingRequest.fields.preconditions')}</label>
                                <small className="block text-color-secondary mb-2">{th('trainingRequest.published.preconditionsHint')}</small>
                                {!formData.trainingType ? (
                                    <div className="text-color-secondary text-sm">
                                        <i className="pi pi-info-circle mr-2" />
                                        {th('trainingRequest.published.preconditionsSelectFirstMessage')}
                                    </div>
                                ) : isLoadingPlannings ? (
                                    <i className="pi pi-spin pi-spinner" />
                                ) : preconditionRows.length === 0 ? (
                                    <div className="text-color-secondary text-sm">{th('trainingRequest.published.preconditionsEmptyMessage')}</div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {preconditionRows.map((row) => (
                                            <Chip key={row.preconditionCode} label={row.preconditionText || row.preconditionCode} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="publishedPlanningId">
                                    {th('trainingRequest.fields.preferTrainingDate')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="publishedPlanningId"
                                    value={formData.planningId}
                                    options={planningOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFormData((prev) => ({ ...prev, planningId: e.value }))}
                                    placeholder={formData.trainingType ? th('trainingRequest.published.selectDatePlaceholder') : th('trainingRequest.published.selectTrainingTypeFirstPlaceholder')}
                                    disabled={!formData.trainingType}
                                    loading={isLoadingPlannings}
                                    className={isFieldInvalid('planningId') ? 'p-invalid' : ''}
                                />
                                {showNoPlanningsMessage && <small className="text-orange-500">{th('trainingRequest.published.noUpcomingPlannings')}</small>}
                                {showEditMismatchHint && <small className="text-orange-500">{th('trainingRequest.published.editPlanningMismatchHint')}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-3">
                            <div className="field">
                                <label>{th('trainingRequest.fields.location')}</label>
                                <InputText value={summaryLocation === th('trainingRequest.summary.none') ? '' : summaryLocation} disabled />
                            </div>
                        </div>

                        <div className="col-12 md:col-3">
                            <div className="field">
                                <label>{th('trainingRequest.fields.trainingLanguage')}</label>
                                <InputText value={summaryLanguage === th('trainingRequest.summary.none') ? '' : summaryLanguage} disabled />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="publishedDescription">{th('trainingRequest.fields.description')}</label>
                                <InputTextarea
                                    id="publishedDescription"
                                    value={formData.description}
                                    rows={3}
                                    autoResize
                                    maxLength={1000}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                    placeholder={th('trainingRequest.fields.descriptionPlaceholder')}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="surface-100 border-round p-3 mt-2">
                        <h3 className="mt-0 mb-1" style={{ fontSize: '1rem' }}>
                            {th('trainingRequest.summary.title')}
                        </h3>
                        <p className="text-color-secondary mt-0 mb-3" style={{ fontSize: '0.85rem' }}>
                            {th('trainingRequest.summary.hint')}
                        </p>
                        <div className="grid text-sm">
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.vehicleModel')}:</strong> {summaryVehicleModel || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.trainingType')}:</strong> {summaryTrainingType || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.numberOfParticipants')}:</strong> {formData.numberOfParticipants ?? th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.participants')}:</strong> <ExpandableNameList names={participantNames} emptyLabel={th('trainingRequest.summary.none')} />
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.preconditions')}:</strong> {summaryPreconditions}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.preferTrainingDate')}:</strong> {summaryDate}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.trainingDay')}:</strong> {selectedPlanning?.trainingDay || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.location')}:</strong> {summaryLocation}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.trainingLanguage')}:</strong> {summaryLanguage}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.fee')}:</strong> {summaryFee}
                            </div>
                            <div className="col-12">
                                <strong>{th('trainingRequest.summary.description')}:</strong> {formData.description || th('trainingRequest.summary.none')}
                            </div>
                        </div>
                    </div>

                    <div className="field-checkbox mt-3">
                        <Checkbox
                            inputId="publishedCommitmentApproval"
                            checked={Number(formData.commitmentApproval) === 1}
                            onChange={(e) => setFormData((prev) => ({ ...prev, commitmentApproval: e.checked ? 1 : 0 }))}
                            className={isFieldInvalid('commitmentApproval') ? 'p-invalid' : ''}
                        />
                        <label htmlFor="publishedCommitmentApproval" className="ml-2 cursor-pointer">
                            {th('trainingRequest.commitment.label')} <span className="text-red-500">*</span>
                        </label>
                    </div>
                </form>
            )}
        </Dialog>
    );
};

export default PublishedTrainingRequestForm;
